import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  InlineEditTextField,
  InputValidationView,
  TextFieldCore,
  Loader,
} from '@adsk/bim360-matrix-react-components';
import T from 'i18n-react';
import IssueFieldInlineEdit from './IssueFieldInlineEdit';
import { issueAttributesType } from '../constants/proptype-validation';
import { presenceValidator } from '../utils/validation-helpers';

class TextFieldInlineEdit extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: '',
      isEditing: false,
      isUpdating: false,
    };
  }

  getErrorMessage() {
    const { hideErrorMessage, isRequired, inputValidator } = this.props;

    if (hideErrorMessage) {
      return null;
    }

    const { inputValue, isEditing } = this.state;
    // Required validation
    if (isEditing && isRequired && !presenceValidator(inputValue)) {
      return T.translate('new.fieldRequired');
    }
    const inputValidationMessage = inputValidator && inputValidator(inputValue);
    if (inputValidationMessage) {
      return inputValidationMessage;
    }
    return '';
  }

  handleInlineEditEnd(value, isCancel, updateIssue, closeEditMode) {
    const { showLoaderOnUpdate } = this.props;

    if (!isCancel && this.isValid()) {
      let onUpdateFinish = null;
      if (showLoaderOnUpdate) {
        this.setState({ isUpdating: true });
        onUpdateFinish = () => {
          this.setState({ isUpdating: false });
          closeEditMode();
        };
      }
      updateIssue(value, onUpdateFinish);
    } else {
      closeEditMode();
    }
  }

  isValid() {
    const { isRequired, inputValidator } = this.props;
    const { inputValue } = this.state;

    const validRequired = !isRequired || presenceValidator(inputValue);
    return (inputValidator ? !inputValidator(inputValue) : true) && validRequired;
  }

  renderLoader() {
    const { showLoaderOnUpdate } = this.props;
    const { isUpdating, isEditing } = this.state;

    // since the loader is not part of the text input, we render a bottom border which is a "continuous" of the text input bottom border
    // this it why we should show the loader container in edit mode event if not yet in update mode
    const showLoader = showLoaderOnUpdate && (isUpdating || isEditing);
    if (!showLoader) {
      return null;
    }

    return (
      <div className="TextFieldInlineEdit__loader-container">
        {isUpdating && (
          <Loader
            className="TextFieldInlineEdit__loader"
            data-testid="TextFieldInlineEdit__loader"
          />
        )}
      </div>
    );
  }

  /**
   * render
   * @returns {XML}
   */
  render() {
    const {
      issue,
      attributeName,
      className,
      label,
      placeholder,
      styleType,
      linkify,
      disabled,
      multiline,
      getFieldValue,
      getIssueAttributes,
      horizontalAlign,
      'data-testid': dataTestId,
    } = this.props;
    const { isEditing } = this.state;
    const classes = classNames('TextFieldInlineEdit', className);
    const errorMessage = this.getErrorMessage();
    const isValid = !(isEditing && errorMessage);
    return (
      <div className={classes}>
        <InputValidationView
          message={errorMessage}
          className="TextFieldInlineEdit__input-validation"
        >
          <IssueFieldInlineEdit
            issue={issue}
            className="TextFieldInlineEdit__field"
            data-testid={dataTestId}
            attributeName={attributeName}
            label={label}
            placeholder={placeholder}
            styleType={styleType}
            linkify={linkify}
            disabled={disabled}
            getFieldValue={getFieldValue}
            getIssueAttributes={getIssueAttributes}
            horizontalAlign={horizontalAlign}
          >
            {(currentValue, closeEditMode, updateIssue) => (
              <InlineEditTextField
                value={currentValue}
                valid={isValid}
                autoselect
                behavior={
                  multiline
                    ? TextFieldCore.Behaviors.MULTI_LINE
                    : TextFieldCore.Behaviors.SINGLE_LINE
                }
                onInlineEditStart={(inputValue) => this.setState({ inputValue, isEditing: true })}
                onChange={(inputValue) => this.setState({ inputValue })}
                onInlineEditEnd={(value, isCancel) => {
                  this.setState({ inputValue: '', isEditing: false });
                  this.handleInlineEditEnd(value, isCancel, updateIssue, closeEditMode);
                }}
              />
            )}
          </IssueFieldInlineEdit>
        </InputValidationView>
        {this.renderLoader()}
      </div>
    );
  }
}

TextFieldInlineEdit.displayName = 'TextFieldInlineEdit';

TextFieldInlineEdit.defaultProps = {
  multiline: true,
  className: undefined,
  'data-testid': undefined,
  label: undefined,
  isRequired: undefined,
  placeholder: undefined,
  styleType: undefined,
  linkify: undefined,
  inputValidator: undefined,
  getFieldValue: undefined,
  getIssueAttributes: undefined,
  disabled: undefined,
  hideErrorMessage: undefined,
  showLoaderOnUpdate: undefined,
  horizontalAlign: undefined,
};

TextFieldInlineEdit.propTypes = {
  issue: issueAttributesType.isRequired,
  attributeName: PropTypes.string.isRequired,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  styleType: PropTypes.oneOf(Object.values(IssueFieldInlineEdit.StyleTypes)),
  linkify: PropTypes.bool,
  inputValidator: PropTypes.func,
  getFieldValue: PropTypes.func,
  getIssueAttributes: PropTypes.func,
  multiline: PropTypes.bool,
  disabled: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  showLoaderOnUpdate: PropTypes.bool,
  horizontalAlign: PropTypes.bool,
};

export default TextFieldInlineEdit;
